import React, { useRef, useState, useEffect } from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, User } from "lucide-react";

const SUGGESTIONS = [
  "Draft a Kaizen event agenda for reducing changeover time.",
  "Write 5 interview questions for a Quality Engineer role.",
  "Explain OEE and how to improve it by 10%.",
  "Create a 30-60-90 day plan for a new Operations Manager.",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => `sess-${Date.now()}`);
  const bottom = useRef(null);

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);

    try {
      const token = localStorage.getItem("ruleai_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "[error contacting AI]" };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 flex flex-col h-full" data-testid="assistant-page">
      <div className="mb-4">
        <div className="overline text-accent">/ RULEAI ASSISTANT</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Ask anything.</h1>
      </div>

      <div className="flex-1 border border-border bg-card flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto p-6 space-y-6" data-testid="assistant-messages">
          {messages.length === 0 && (
            <div className="max-w-2xl">
              <div className="overline text-muted-foreground mb-3">Try one of these</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s} onClick={() => send(s)}
                    data-testid={`suggestion-${s.substring(0, 12)}`}
                    className="text-left border border-border p-4 hover:bg-secondary transition-colors text-sm rounded-sm"
                  >
                    <Sparkles className="w-4 h-4 text-accent mb-2" />
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className="flex gap-3" data-testid={`msg-${m.role}-${i}`}>
              <div className={`w-8 h-8 shrink-0 grid place-items-center rounded-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-zinc-950 text-accent"}`}>
                {m.role === "user" ? <User className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              </div>
              <div className="flex-1">
                <div className="overline text-muted-foreground mb-1">
                  {m.role === "user" ? "You" : "RuleAI · Claude Sonnet 4.6"}
                </div>
                <div className="text-sm leading-relaxed whitespace-pre-wrap font-mono-plex">
                  {m.content}
                  {busy && i === messages.length - 1 && m.role === "assistant" && <span className="kinetic" />}
                </div>
              </div>
            </div>
          ))}
          <div ref={bottom} />
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-border p-4 flex gap-2"
        >
          <Input
            placeholder="Message RuleAI…"
            value={input} onChange={(e) => setInput(e.target.value)}
            disabled={busy}
            data-testid="assistant-input"
            className="rounded-sm"
          />
          <Button
            type="submit" disabled={busy || !input.trim()}
            data-testid="assistant-send"
            className="rounded-sm bg-accent hover:bg-accent/90 text-white"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
